/**
 * User Types for v2v2 SWFL Farmers Market Application
 * Defines user profiles, preferences, and authentication states
 */

export type UserRole = 'user' | 'vendor' | 'admin';

export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role: UserRole;
  createdAt: Date;
  updatedAt: Date;
  preferences: UserPreferences;
  location?: UserLocation;
  vendorId?: string;
}

export interface UserPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  newsletter: boolean;
  defaultRadius: number;
  preferredCategories: string[];
  dietaryPreferences: string[];
  theme: 'light' | 'dark' | 'system';
}

export interface UserLocation {
  city: string;
  state: string;
  zipCode: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface AuthState {
  user: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface RegisterData extends AuthCredentials {
  displayName: string;
  acceptTerms: boolean;
}

export interface PasswordResetData {
  email: string;
}

export interface UpdateProfileData {
  displayName?: string;
  photoURL?: string;
  preferences?: Partial<UserPreferences>;
  location?: UserLocation;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  emailNotifications: true,
  pushNotifications: false,
  newsletter: false,
  defaultRadius: 25,
  preferredCategories: [],
  dietaryPreferences: [],
  theme: 'system',
};

export function createDefaultProfile(
  id: string,
  email: string,
  displayName: string | null = null
): UserProfile {
  const now = new Date();
  return {
    id,
    email,
    displayName,
    photoURL: null,
    role: 'user',
    createdAt: now,
    updatedAt: now,
    preferences: { ...DEFAULT_PREFERENCES },
  };
}
